import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import ProductCard from '../components/product/ProductCard'
import SearchFilters from '../components/search/SearchFilters'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { searchProducts } from '../services/api'

function SearchPage() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const query = searchParams.get('q') || 'all'
  const category = searchParams.get('category')

  const [results, setResults] = useState({ products: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    category: category || 'all',
    priceRange: [0, 10000],
    minRating: 0,
    inStock: false,
    tradeAssurance: false,
    verified: false,
    maxMoq: null,
    sortBy: 'relevance'
  })

  const getLocalized = (obj) => {
    if (!obj) return ''
    if (typeof obj === 'string') return obj
    return obj[i18n.language] || obj['en'] || obj['de'] || ''
  }

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true)
      try {
        const data = await searchProducts(query, {
          ...filters,
          lang: i18n.language
        })
        setResults(data)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [query, filters, i18n.language])

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": results.products.slice(0, 10).map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": getLocalized(product.name),
        "description": getLocalized(product.description),
        "url": `https://1688euro.com/product/${product.id}`,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "EUR",
          "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      }
    }))
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Helmet>
        <title>{query !== 'all' ? `${query} - ` : ''}{t('search.resultsFor')} | 1688Euro.com</title>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <SearchFilters filters={filters} onFilterChange={handleFilterChange} />
          </div>

          {/* Results Area */}
          <div className="flex-1">
            <div className="mb-6 flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">
                {results.total} {t('search.productsFound')}
                {query !== 'all' && <span className="text-gray-500 font-normal ml-2">für "{query}"</span>}
              </h1>

              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange({ sortBy: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-[#FF6600]"
              >
                <option value="relevance">{t('search.sort.relevance')}</option>
                <option value="price_asc">{t('search.sort.price_asc')}</option>
                <option value="price_desc">{t('search.sort.price_desc')}</option>
                <option value="newest">{t('search.sort.newest')}</option>
              </select>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <LoadingSpinner size="lg" />
              </div>
            ) : results.products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {results.products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="bg-white p-12 text-center rounded-lg border border-gray-200">
                <p className="text-gray-500 text-lg">{t('search.noResults')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchPage